export type Currency = 'EUR' | 'USD' | 'GBP';
export type CurrencyCode = string;

import { euroIcon, usdIcon, gbrIcon } from '@peanuds/ui';

type FormatCurrencyMode = 'symbol' | 'symbolWithCode' | 'code';

type FormatCurrencyOptions = {
  mode?: FormatCurrencyMode;
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

export const DEFAULT_TRANSACTION_AMOUNT_PATTERN = '{sign} {currencySymbol}{amount}';

/**
 * Default display locale for amounts. `de-DE` renders 5.555,00.
 * Customers may override it via `customerConfig.currency.locale`.
 */
export const DEFAULT_AMOUNT_LOCALE = 'de-DE';

let amountLocale = DEFAULT_AMOUNT_LOCALE;

export const getAmountLocale = (): string => amountLocale;

export const setAmountLocale = (locale: string): void => {
  amountLocale = locale;
};

export type TransactionAmountFormatConfig = {
  pattern?: string;
  locale?: Intl.LocalesArgument;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

export const formatCurrency = (
  amount: number = 0,
  currency?: CurrencyCode,
  options?: FormatCurrencyOptions,
) => {
  const code = currency || 'EUR';
  const mode = options?.mode ?? 'symbol';
  const locale = options?.locale ?? getAmountLocale();
  const minimumFractionDigits =
    options?.minimumFractionDigits ?? options?.maximumFractionDigits ?? 2;
  const maximumFractionDigits =
    options?.maximumFractionDigits ?? options?.minimumFractionDigits ?? 2;

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
    style: 'currency',
    currency: code,
  });

  const parts = formatter.formatToParts(amount);

  const symbol = parts.find(({ type }) => type === 'currency')?.value ?? '';
  const number = parts
    .map(({ type, value }) => (type !== 'currency' ? value : ''))
    .join('')
    .trim();

  if (mode === 'code') return `${number} ${code}`;
  if (mode === 'symbolWithCode') return `${symbol} ${number} ${code}`;
  return `${symbol} ${number}`;
};

export const formatWeaverCurrency = (amount: number = 0, currency?: CurrencyCode) => {
  return formatCurrency(amount, currency);
};

export const currencyIconMap: Record<Currency, string> = {
  EUR: euroIcon,
  USD: usdIcon,
  GBP: gbrIcon,
};

/**
 * Get the icon URL for a given currency code
 * @param code - The currency code (e.g., 'EUR', 'USD', 'GBP')
 * @returns The icon URL string, or undefined if currency is not supported
 */
export const getCurrencyIcon = (code: string): string | undefined => {
  return currencyIconMap[code as Currency];
};

/**
 * Currency-related pure utilities (no UI, no feature dependencies).
 */

/**
 * Returns the localized currency symbol for a given ISO currency code.
 * Falls back to the code itself if Intl fails.
 */
export const getCurrencySymbol = (code: string, locale: Intl.LocalesArgument = 'en-US'): string => {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? code;
  } catch {
    return code;
  }
};

export const formatSignedCurrencyAmount = (
  amount: number,
  currency: CurrencyCode = 'EUR',
  direction: string | undefined,
  config: TransactionAmountFormatConfig = {},
): string => {
  const normalizedDirection = direction?.toLowerCase();
  const signedAmount = normalizedDirection === 'out' ? -Math.abs(amount) : Math.abs(amount);
  const sign = signedAmount < 0 ? '-' : '+';
  const locale = config.locale ?? getAmountLocale();
  const amountText = new Intl.NumberFormat(locale, {
    minimumFractionDigits: config.minimumFractionDigits ?? 2,
    maximumFractionDigits: config.maximumFractionDigits ?? 2,
  }).format(Math.abs(signedAmount));
  const currencySymbol = getCurrencySymbol(currency, locale);
  const pattern = config.pattern ?? DEFAULT_TRANSACTION_AMOUNT_PATTERN;

  return pattern
    .split('{sign}')
    .join(sign)
    .split('{currencySymbol}')
    .join(currencySymbol)
    .split('{currencyCode}')
    .join(currency)
    .split('{amount}')
    .join(amountText);
};
